/**
 * Feature flags system for demo-specific functionality
 * Provides runtime control over demo features and capabilities
 */

import { getFinalDemoConfig } from '../config/demo.config';

export type FeatureFlag = 
  | 'providerFailover'
  | 'settlementRailDemo'
  | 'costCalculator'
  | 'professionalBranding'
  | 'demoControlPanel'
  | 'offlineMode';

class FeatureFlagManager {
  private config = getFinalDemoConfig();
  private overrides: Partial<Record<FeatureFlag, boolean>> = {};

  /**
   * Check if a feature is enabled
   */
  isEnabled(flag: FeatureFlag): boolean {
    // Check for runtime overrides first
    if (this.overrides[flag] !== undefined) {
      return this.overrides[flag]!;
    }
    
    // Fall back to configuration
    return this.config.features[flag] || false;
  }

  /**
   * Enable a feature at runtime
   */
  enable(flag: FeatureFlag): void {
    this.overrides[flag] = true;
    this.notifyChange(flag, true);
  }

  /**
   * Disable a feature at runtime
   */
  disable(flag: FeatureFlag): void {
    this.overrides[flag] = false;
    this.notifyChange(flag, false);
  }

  /**
   * Toggle a feature at runtime
   */
  toggle(flag: FeatureFlag): boolean {
    const newValue = !this.isEnabled(flag);
    this.overrides[flag] = newValue;
    this.notifyChange(flag, newValue);
    return newValue;
  }

  /**
   * Reset a feature to its configuration default
   */
  reset(flag: FeatureFlag): void {
    delete this.overrides[flag];
    this.notifyChange(flag, this.config.features[flag] || false);
  }

  /**
   * Reset all features to configuration defaults
   */
  resetAll(): void {
    this.overrides = {};
    Object.keys(this.config.features).forEach(flag => {
      this.notifyChange(flag as FeatureFlag, this.config.features[flag as FeatureFlag] || false);
    });
  }

  /**
   * Get all feature states
   */
  getAllFeatures(): Record<FeatureFlag, boolean> {
    const features: Partial<Record<FeatureFlag, boolean>> = {};
    
    Object.keys(this.config.features).forEach(flag => {
      features[flag as FeatureFlag] = this.isEnabled(flag as FeatureFlag);
    });
    
    return features as Record<FeatureFlag, boolean>;
  }

  /**
   * Set multiple features at once
   */
  setFeatures(features: Partial<Record<FeatureFlag, boolean>>): void {
    Object.entries(features).forEach(([flag, enabled]) => {
      if (enabled !== undefined) {
        this.overrides[flag as FeatureFlag] = enabled;
        this.notifyChange(flag as FeatureFlag, enabled);
      }
    });
  }

  /**
   * Get feature configuration for debugging
   */
  getDebugInfo(): {
    config: Record<FeatureFlag, boolean>;
    overrides: Partial<Record<FeatureFlag, boolean>>;
    final: Record<FeatureFlag, boolean>;
  } {
    return {
      config: this.config.features,
      overrides: this.overrides,
      final: this.getAllFeatures(),
    };
  }

  private notifyChange(flag: FeatureFlag, enabled: boolean): void {
    // Emit custom event for components to listen to
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('featureFlagChange', {
        detail: { flag, enabled }
      }));
    }
    
    // Log in development mode
    if (this.config.analytics.debugMode) {
      console.log(`Feature flag ${flag} ${enabled ? 'enabled' : 'disabled'}`);
    }
  }
}

// Singleton instance
export const featureFlags = new FeatureFlagManager();

// React hook for using feature flags
export const useFeatureFlag = (flag: FeatureFlag): boolean => {
  const [enabled, setEnabled] = React.useState(featureFlags.isEnabled(flag));

  React.useEffect(() => {
    const handleFeatureFlagChange = (event: CustomEvent) => {
      if (event.detail.flag === flag) {
        setEnabled(event.detail.enabled);
      }
    };

    window.addEventListener('featureFlagChange', handleFeatureFlagChange as EventListener);
    
    return () => {
      window.removeEventListener('featureFlagChange', handleFeatureFlagChange as EventListener);
    };
  }, [flag]);

  return enabled;
};

// React hook for managing multiple feature flags
export const useFeatureFlags = (): {
  features: Record<FeatureFlag, boolean>;
  enable: (flag: FeatureFlag) => void;
  disable: (flag: FeatureFlag) => void;
  toggle: (flag: FeatureFlag) => boolean;
  reset: (flag: FeatureFlag) => void;
  resetAll: () => void;
} => {
  const [features, setFeatures] = React.useState(featureFlags.getAllFeatures());

  React.useEffect(() => {
    const handleFeatureFlagChange = () => {
      setFeatures(featureFlags.getAllFeatures());
    };

    window.addEventListener('featureFlagChange', handleFeatureFlagChange);
    
    return () => {
      window.removeEventListener('featureFlagChange', handleFeatureFlagChange);
    };
  }, []);

  return {
    features,
    enable: featureFlags.enable.bind(featureFlags),
    disable: featureFlags.disable.bind(featureFlags),
    toggle: featureFlags.toggle.bind(featureFlags),
    reset: featureFlags.reset.bind(featureFlags),
    resetAll: featureFlags.resetAll.bind(featureFlags),
  };
};

// Utility functions for common feature checks
export const isDemoMode = (): boolean => featureFlags.isEnabled('demoControlPanel');
export const isProviderFailoverEnabled = (): boolean => featureFlags.isEnabled('providerFailover');
export const isSettlementRailDemoEnabled = (): boolean => featureFlags.isEnabled('settlementRailDemo');
export const isCostCalculatorEnabled = (): boolean => featureFlags.isEnabled('costCalculator');
export const isProfessionalBrandingEnabled = (): boolean => featureFlags.isEnabled('professionalBranding');
export const isOfflineModeEnabled = (): boolean => featureFlags.isEnabled('offlineMode');

// Add React import for hooks
import React from 'react';